import axiosInstance from 'shared/services/api/axiosInstance';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';
import { isDemoTeamActive } from 'shared/utils/demoTeam';

const teamHeaders = () => {
  const id = getSelectedTeamNumericId();
  return id != null ? { 'X-Team-Id': String(id) } : {};
};

// Demo workspace = viewer-only. Existing workflows load from the backend, but
// any create/edit is kept purely in the browser (never saved) so users can play
// with the builder without errors. Cleared on reload.
let _demoWfSeq = 0;
const _demoWorkflows = new Map();
const nowIso = () => new Date().toISOString();

export async function listWorkflows({ page = 1, limit = 25, q = '', status = '' } = {}) {
  const params = { page, limit };
  if (q) params.q = q;
  if (status) params.status = status;
  const { data } = await axiosInstance.get('/workflows', { headers: teamHeaders(), params });
  const workflows = data.workflows || [];
  return {
    workflows,
    pagination: data.pagination || { page, limit, total: workflows.length, totalPages: 1 },
  };
}

export async function createWorkflow({ name, description, sidebar_folder } = {}) {
  if (isDemoTeamActive()) {
    _demoWfSeq += 1;
    const wf = {
      id: `demo-wf-${_demoWfSeq}`, name: name || 'Untitled workflow', description: description || null,
      sidebar_folder: sidebar_folder || null, status: 'draft_only',
      draft_definition: { nodes: [], edges: [], trigger: null }, published_version_id: null,
      created_at: nowIso(), updated_at: nowIso(), _demo: true,
    };
    _demoWorkflows.set(wf.id, wf);
    return wf;
  }
  const { data } = await axiosInstance.post(
    '/workflows',
    { name, description, sidebar_folder },
    { headers: teamHeaders() },
  );
  return data.workflow;
}

export async function getWorkflow(id) {
  if (_demoWorkflows.has(id)) return _demoWorkflows.get(id);
  const { data } = await axiosInstance.get(`/workflows/${id}`, { headers: teamHeaders() });
  return data.workflow;
}

export async function patchWorkflow(id, patch) {
  if (isDemoTeamActive()) {
    const wf = { ...(_demoWorkflows.get(id) || { id }), ...patch, updated_at: nowIso() };
    if (_demoWorkflows.has(id)) _demoWorkflows.set(id, wf);
    return wf;
  }
  const { data } = await axiosInstance.patch(`/workflows/${id}`, patch, { headers: teamHeaders() });
  return data.workflow;
}

export async function saveDraft(id, definition) {
  if (isDemoTeamActive()) {
    // Keep edits in-memory only — never persist to the shared demo team.
    const wf = { ...(_demoWorkflows.get(id) || { id }), draft_definition: definition, updated_at: nowIso() };
    if (_demoWorkflows.has(id)) _demoWorkflows.set(id, wf);
    return wf;
  }
  const { data } = await axiosInstance.put(
    `/workflows/${id}/draft`,
    { definition },
    { headers: teamHeaders() },
  );
  return data.workflow;
}

export async function publishWorkflow(id) {
  if (isDemoTeamActive()) {
    return { id: `demo-ver-${id}`, workflow_id: id, version_number: 1, published_at: nowIso(), _demo: true };
  }
  const { data } = await axiosInstance.post(
    `/workflows/${id}/publish`,
    {},
    { headers: teamHeaders() },
  );
  return data.version;
}

// { versions, published_version_id, total, limit }. `limit` (max 50, default 10)
// asks for older versions; rows carry published_by_name and node_count.
export async function listWorkflowVersions(id, { limit } = {}) {
  const params = {};
  if (limit) params.limit = limit;
  const { data } = await axiosInstance.get(`/workflows/${id}/versions`, { headers: teamHeaders(), params });
  return data;
}

export async function getWorkflowVersion(workflowId, versionId) {
  const { data } = await axiosInstance.get(
    `/workflows/${workflowId}/versions/${versionId}`,
    { headers: teamHeaders() },
  );
  return data.version;
}

export async function republishWorkflowVersion(workflowId, versionId) {
  const { data } = await axiosInstance.post(
    `/workflows/${workflowId}/versions/${versionId}/republish`,
    {},
    { headers: teamHeaders() },
  );
  return data.version;
}

export async function restoreWorkflowVersion(workflowId, versionId) {
  const { data } = await axiosInstance.post(
    `/workflows/${workflowId}/versions/${versionId}/restore`,
    {},
    { headers: teamHeaders() },
  );
  return data;
}

export async function setWorkflowStatus(id, status) {
  if (isDemoTeamActive()) {
    const wf = { ...(_demoWorkflows.get(id) || { id }), status, updated_at: nowIso() };
    if (_demoWorkflows.has(id)) _demoWorkflows.set(id, wf);
    return wf;
  }
  const { data } = await axiosInstance.post(
    `/workflows/${id}/status`,
    { status },
    { headers: teamHeaders() },
  );
  return data.workflow;
}

/**
 * Enable, disable or delete up to 100 agents in one request.
 * Resolves { results: [{ id, ok, error?: 'not_published' | 'not_found' | 'failed' }] }.
 */
export async function bulkWorkflows(ids, action) {
  if (isDemoTeamActive()) return { results: ids.map((id) => ({ id, ok: false, error: 'read_only' })) };
  const { data } = await axiosInstance.post('/workflows/bulk', { ids, action }, { headers: teamHeaders() });
  return { results: data?.results || [] };
}

export async function deleteWorkflow(id) {
  if (isDemoTeamActive()) { _demoWorkflows.delete(id); return true; }
  const { data } = await axiosInstance.delete(`/workflows/${id}`, { headers: teamHeaders() });
  return data.ok;
}

export async function rotateWebhookSecret(id) {
  const { data } = await axiosInstance.post(
    `/workflows/${id}/rotate-webhook`,
    {},
    { headers: teamHeaders() },
  );
  return data.webhook_secret;
}

// Executions (P2)
export async function startManualExecution(id, payload) {
  const { data } = await axiosInstance.post(
    `/workflows/${id}/executions`,
    { input: payload || {} },
    { headers: teamHeaders() },
  );
  return data.execution;
}

export async function listExecutions(id) {
  const { data } = await axiosInstance.get(`/workflows/${id}/executions`, { headers: teamHeaders() });
  return data.executions || [];
}

/**
 * One page of an agent's runs: { executions, next_cursor, stats }. Pass the
 * previous `next_cursor` as `cursor` for the next page. `status` is
 * succeeded | failed | running | cancelled; `versionId` limits to one version.
 */
export async function listExecutionsPage(id, { limit, cursor, status, versionId } = {}) {
  const params = {};
  if (limit) params.limit = limit;
  if (cursor) params.cursor = cursor;
  if (status) params.status = status;
  if (versionId) params.version_id = versionId;
  const { data } = await axiosInstance.get(`/workflows/${id}/executions`, { headers: teamHeaders(), params });
  return {
    executions: data.executions || [],
    next_cursor: data.next_cursor || null,
    stats: data.stats || null,
  };
}

/** A run without step input/output/logs (cheap to poll); open a step with getExecutionStep. */
export async function getExecutionLite(execId) {
  const { data } = await axiosInstance.get(`/workflows/executions/${execId}`, { headers: teamHeaders(), params: { lite: 1 } });
  return data.execution;
}

/** Run an earlier run again (same version and trigger payload); returns the new run. */
export async function retryExecution(execId) {
  const { data } = await axiosInstance.post(`/workflows/executions/${execId}/retry`, {}, { headers: teamHeaders() });
  return data.execution;
}

export async function getExecution(execId) {
  const { data } = await axiosInstance.get(`/workflows/executions/${execId}`, { headers: teamHeaders() });
  return data.execution;
}

// Most-recent finished step for a node — one indexed query, powers the editor's
// Input/Output sample panes (replaces a client-side loop over full executions).
export async function getLatestStepForNode(workflowId, nodeId) {
  const { data } = await axiosInstance.get(
    `/workflows/${workflowId}/nodes/${encodeURIComponent(nodeId)}/latest-step`,
    { headers: teamHeaders() },
  );
  return data.step || null;
}

// Full (untruncated) single step — for expanding a capped input/output field.
export async function getExecutionStep(execId, stepId) {
  const { data } = await axiosInstance.get(
    `/workflows/executions/${execId}/steps/${stepId}`,
    { headers: teamHeaders() },
  );
  return data.step || null;
}

export async function cancelExecution(execId) {
  const { data } = await axiosInstance.post(
    `/workflows/executions/${execId}/cancel`,
    {},
    { headers: teamHeaders() },
  );
  return data.execution;
}

export async function testStep(id, body) {
  const { data } = await axiosInstance.post(
    `/workflows/${id}/test-step`,
    body,
    { headers: teamHeaders() },
  );
  return data;
}

// Editor "Test": run the live definition up to `untilNodeId`, returning
// { nodes: { [nodeId]: { input, output, status, error, logs } } }. Real data
// fetches execute and each node's output feeds the next — no history write.
export async function testRunWorkflow(id, { definition, untilNodeId, inputsOnly = false }) {
  const { data } = await axiosInstance.post(
    `/workflows/${id}/test-run`,
    { definition, until_node_id: untilNodeId, inputs_only: inputsOnly },
    { headers: teamHeaders() },
  );
  return data?.nodes || {};
}

// Credentials
export async function listCredentials() {
  const { data } = await axiosInstance.get('/workflows/credentials', { headers: teamHeaders() });
  return data.credentials || [];
}

export async function createCredential(body) {
  const { data } = await axiosInstance.post('/workflows/credentials', body, { headers: teamHeaders() });
  return data;  // returns { credential, test }
}

export async function testCredential(id) {
  const { data } = await axiosInstance.post(`/workflows/credentials/${id}/test`, {}, { headers: teamHeaders() });
  return data;
}

export async function deleteCredential(id) {
  const { data } = await axiosInstance.delete(`/workflows/credentials/${id}`, { headers: teamHeaders() });
  return data.ok;
}

// Data access (P3)
export async function queryData(spec) {
  const { data } = await axiosInstance.post('/workflows/data/query', spec, { headers: teamHeaders() });
  return data;
}

export async function runSql(body) {
  const { data } = await axiosInstance.post('/workflows/data/sql', body, { headers: teamHeaders() });
  return data;
}

// MCP (P7)
export async function mcpDiscover(body) {
  const { data } = await axiosInstance.post('/workflows/mcp/discover', body, { headers: teamHeaders() });
  return data;
}

export async function mcpListTools(credentialId) {
  const { data } = await axiosInstance.get(`/workflows/mcp/${credentialId}/tools`, { headers: teamHeaders() });
  return data;
}

// Test an MCP server connection and, on success, save it as a reusable team
// credential (returns { credential, tools }). Throws with the server's error
// message on failure so the UI can surface it.
export async function mcpConnect(body) {
  const { data } = await axiosInstance.post('/workflows/mcp/connect', body, { headers: teamHeaders() });
  return data;
}

// Model integrations (shared with Evals — lives in team_llm_integrations).
// Used by the AI Agent + LLM nodes' credential picker.
export async function listModelIntegrations() {
  const teamId = getSelectedTeamNumericId();
  const { data } = await axiosInstance.get('/evals/integrations', {
    params: teamId != null ? { team_id: teamId } : {},
  });
  return data?.integrations || [];
}

// Slack workspace connection status (used by workflow Slack nodes to show
// whether the team has connected a workspace, with a CTA to /integrations).
export async function getSlackConnection() {
  const teamId = getSelectedTeamNumericId();
  const params = teamId != null ? { team_id: teamId } : {};
  const headers = teamHeaders();
  const { data } = await axiosInstance.get('/integrations/slack/org', { params, headers });
  return data || { connected: false };
}

export async function getSlackChannels({ refresh, operation } = {}) {
  const teamId = getSelectedTeamNumericId();
  const { data } = await axiosInstance.get('/integrations/slack/channels', {
    params: {
      ...(teamId != null ? { team_id: teamId } : {}),
      ...(refresh ? { refresh: '1' } : {}),
      ...(operation ? { operation } : {}),
    },
    headers: teamHeaders(),
  });
  return data?.channels || [];
}

export async function getSlackUsers({ refresh } = {}) {
  const teamId = getSelectedTeamNumericId();
  const { data } = await axiosInstance.get('/integrations/slack/users', {
    params: {
      ...(teamId != null ? { team_id: teamId } : {}),
      ...(refresh ? { refresh: '1' } : {}),
    },
    headers: teamHeaders(),
  });
  return data?.users || [];
}

export async function getSlackMembers({ channel, refresh } = {}) {
  const teamId = getSelectedTeamNumericId();
  const { data } = await axiosInstance.get('/integrations/slack/members', {
    params: {
      ...(teamId != null ? { team_id: teamId } : {}),
      ...(channel ? { channel } : {}),
      ...(refresh ? { refresh: '1' } : {}),
    },
    headers: teamHeaders(),
  });
  return data?.members || [];
}

export async function getSlackUserGroups() {
  const teamId = getSelectedTeamNumericId();
  const { data } = await axiosInstance.get('/integrations/slack/usergroups', {
    params: teamId != null ? { team_id: teamId } : {},
    headers: teamHeaders(),
  });
  return data?.usergroups || [];
}

export async function getSlackFiles({ channel, user } = {}) {
  const teamId = getSelectedTeamNumericId();
  const { data } = await axiosInstance.get('/integrations/slack/files', {
    params: {
      ...(teamId != null ? { team_id: teamId } : {}),
      ...(channel ? { channel } : {}),
      ...(user ? { user } : {}),
    },
    headers: teamHeaders(),
  });
  return data?.files || [];
}

export async function getSlackMessages({ channel } = {}) {
  const teamId = getSelectedTeamNumericId();
  const { data } = await axiosInstance.get('/integrations/slack/messages', {
    params: {
      ...(teamId != null ? { team_id: teamId } : {}),
      ...(channel ? { channel } : {}),
    },
    headers: teamHeaders(),
  });
  return data?.messages || [];
}

export async function getLinearConnection() {
  const teamId = getSelectedTeamNumericId();
  const { data } = await axiosInstance.get('/integrations/linear', {
    params: teamId != null ? { team_id: teamId } : {},
    headers: teamHeaders(),
  });
  return data || { connected: false };
}

/** Linear teams for workflow node team pickers (team OAuth). */
export async function getLinearTeams() {
  const teamId = getSelectedTeamNumericId();
  const params = teamId != null ? { team_id: teamId } : {};
  const { data } = await axiosInstance.get('/integrations/linear/teams', {
    params,
    headers: teamHeaders(),
  });
  return data?.teams || [];
}

/** Workflow states for a Linear team (used by Update issue state picker). */
export async function getLinearStates(linearTeamId) {
  const teamId = getSelectedTeamNumericId();
  const params = { linear_team_id: linearTeamId };
  if (teamId != null) params.team_id = teamId;
  const { data } = await axiosInstance.get('/integrations/linear/states', {
    params,
    headers: teamHeaders(),
  });
  return data?.states || [];
}

/** Team members for Linear assignee pickers. */
export async function getLinearAssignees(linearTeamId) {
  const teamId = getSelectedTeamNumericId();
  const params = { linear_team_id: linearTeamId };
  if (teamId != null) params.team_id = teamId;
  const { data } = await axiosInstance.get('/integrations/linear/assignees', {
    params,
    headers: teamHeaders(),
  });
  return data?.assignees || [];
}

// Filter schema (events / runs / spans / users)
export async function getFilterSchema(entity, eventName) {
  const params = { entity };
  if (eventName) params.event_name = eventName;
  const { data } = await axiosInstance.get('/workflows/schema', {
    headers: teamHeaders(),
    params,
  });
  return data;
}

export async function getPropertyValues({ entity, event, property, limit }) {
  const { data } = await axiosInstance.get('/workflows/schema/property-values', {
    headers: teamHeaders(),
    params: { entity, event, property, limit: limit || 50 },
  });
  return (data.values || []).map((v) =>
    (v && typeof v === 'object' && 'value' in v) ? v.value : v,
  );
}

/** The team's GitHub App install ({installation_id, github_login}) or null. */
export async function getGithubIntegration() {
  const { data } = await axiosInstance.get('/integrations/github', { headers: teamHeaders(), params: { team_id: getSelectedTeamNumericId() } });
  return data?.integration || null;
}

// ── Templates (TRO-499) ──────────────────────────────────────────────────────
export async function listAgentTemplates() {
  const { data } = await axiosInstance.get('/workflows/templates', { headers: teamHeaders() });
  return data?.templates || [];
}

/** { templates, categories }; also accepts the older plain-array response (categories = []). */
export async function listAgentTemplateCatalog() {
  const { data } = await axiosInstance.get('/workflows/templates', { headers: teamHeaders() });
  if (Array.isArray(data)) return { templates: data, categories: [], needs: {} };
  return { templates: data?.templates || [], categories: data?.categories || [], needs: data?.needs || {} };
}

/** Create a DRAFT agent from a template; returns { workflow, missing }. */
export async function createAgentFromTemplate(template, params) {
  const { data } = await axiosInstance.post('/workflows/from-template', { template, params }, { headers: teamHeaders() });
  return data;
}

/** Repos linked to the team's GitHub App ({owner, name}). */
export async function listLinkedGithubRepos() {
  const { data } = await axiosInstance.get('/integrations/github/repos', { headers: teamHeaders(), params: { team_id: getSelectedTeamNumericId() } });
  return data?.items || [];
}
